"use client";

import { useState, useMemo, useEffect, useCallback } from 'react';
import Link from 'next/link';
import ProductGrid, { Product } from './ProductGridClient';
import FilterList from './FilterList';
import type { FilterPayload, Attribute } from './FilterList';
import { API_BASE_URL } from '@/app/lib/api';
import WaveHeader from '@/app/Components/home/WaveHeader';

interface Subcategory { slug: string; name: string; productFamily?: string | null }
interface Category { id: string; name: string; slug?: string; parent?: { id?: string; name?: string; slug?: string; colorCataloge?: string | null } | null; colorCataloge?: string | null }

interface CategoryViewProps {
  parentCategoryName?: string;
  subcategories: Subcategory[];
  initialProducts: Product[];
  category?: Category;
  allCategories?: { id: string; name: string; slug?: string; image?: string | null }[];
  attributes?: Attribute[];
}

export default function CategoryView({ parentCategoryName, subcategories, initialProducts, category, allCategories, attributes = [] }: CategoryViewProps) {
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>(initialProducts ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attributeFilters, setAttributeFilters] = useState<Record<string, string[]>>({});

  // Group subcategories by productFamily
  const groupedSubcategories = useMemo(() => {
    if (!subcategories || subcategories.length === 0) return [];
    const map = new Map<string, Subcategory[]>();
    for (const sub of subcategories) {
      const family = (sub.productFamily && sub.productFamily.trim()) ? sub.productFamily.trim() : '';
      if (!map.has(family)) map.set(family, []);
      map.get(family)!.push(sub);
    }
    const entries = Array.from(map.entries()).sort((a, b) => {
      if (a[0] === 'Autres') return 1;
      if (b[0] === 'Autres') return -1;
      return a[0].localeCompare(b[0]);
    });
    return entries.map(([family, subs]) => ({ family, subs }));
  }, [subcategories]);

  // Build query URL for backend
  const buildUrl = useCallback((slug: string, filters: Record<string, string[]> | undefined, skip = 0, take = 24) => {
    const params = new URLSearchParams();
    if (typeof skip === 'number') params.set('skip', String(skip));
    if (typeof take === 'number') params.set('take', String(take));

    if (filters && Object.keys(filters).length > 0) {
      // flatten to attributeValueIds
      const flat = Object.values(filters).flat();
      if (flat.length > 0) params.set('attributeValueIds', Array.from(new Set(flat)).join(','));
      // per-attribute params (attr_<attrId>=val1,val2)
      for (const [k, vals] of Object.entries(filters)) {
        if (!vals || vals.length === 0) continue;
        params.set(`attr_${k}`, vals.join(','));
      }
    }

    return `${API_BASE_URL}/categories/${encodeURIComponent(slug)}/products?${params.toString()}`;
  }, []);

  // Fetch products for a given slug (category or subcategory)
  const fetchProductsFor = useCallback(async (targetSlug: string | undefined) => {
    if (!targetSlug) return;
    setLoading(true);
    setError(null);
    try {
      const url = buildUrl(targetSlug, attributeFilters, 0, 48);
      const res = await fetch(url);
      if (!res.ok) {
        setError(`Erreur ${res.status}`);
        setProducts([]);
      } else {
        const body = await res.json();
        // API returns { products, total, categories }
        if (Array.isArray(body.products)) setProducts(body.products as Product[]);
        else if (Array.isArray(body)) setProducts(body as Product[]);
        else setProducts([]);
      }
    } catch (e) {
      setError('Erreur réseau');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [attributeFilters, buildUrl]);

  // When activeSlug or attributeFilters change, fetch data from backend.
  useEffect(() => {
    // target slug: prefer selected (activeSlug) else current page category slug
    const target = activeSlug ?? category?.slug;
    // If no category slug available, keep client-side products
    if (!target) return;
    fetchProductsFor(target);
  }, [activeSlug, attributeFilters, category?.slug, fetchProductsFor]);

  // Keep initialProducts in sync on first render
  useEffect(() => {
    setProducts(initialProducts ?? []);
  }, [initialProducts]);

  const handleFilterSelect = (slug: string | null) => {
    setActiveSlug(slug);
  };

  const handleAttributesChange = useCallback((payload: FilterPayload | Record<string, any>) => {
    // FilterList emits a payload with shape { attributes, categories, gammes, categorySlugs, gammeSlugs }
    // We only need the attributes map for attribute filters, and categorySlugs to set activeSlug.
    const attrs = (payload as FilterPayload).attributes ?? {};
    setAttributeFilters(attrs);

    const categorySlugs = (payload as FilterPayload).categorySlugs ?? [];
    if (Array.isArray(categorySlugs) && categorySlugs.length > 0) {
      // Use the first selected category slug as the active filter
      setActiveSlug(categorySlugs[0] ?? null);
    } else {
      // If no category selected from filter panel, reset to null to use the page category
      setActiveSlug(null);
    }
  }, []);

  const decodeSlugToName = (s?: string) => {
    if (!s) return '';
    try {
      const decoded = decodeURIComponent(String(s));
      return decoded.replace(/[-_]+/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
    } catch (e) {
      return String(s).replace(/[-_]+/g, ' ');
    }
  };

  const activeCategoryName = activeSlug === null
    ? (category?.name ?? parentCategoryName ?? "Tous les produits")
    : (
      subcategories.find(s => s.slug === activeSlug)?.name
      ?? allCategories?.find(c => c.slug === activeSlug)?.name
      ?? decodeSlugToName(activeSlug)
    );

  const effectiveCatalogColor = category?.parent?.colorCataloge ?? category?.colorCataloge ?? '#3B82F6';

  return (
    <div>
      {/* Wave header displays the active category name and breadcrumb (client-side reactive) */}
      <WaveHeader
        title={activeCategoryName}
        subtitle={''}
        controls={(
          <nav className="flex items-center space-x-2 text-sm text-white/90">
            <a href="/" className="hover:text-white">Accueil</a>
            <span>/</span>
            {category?.parent && (
              <>
                <Link href={`/categorie/${category.parent.slug}`} className="hover:text-white">
                  {category.parent.name}
                </Link>
                <span>/</span>
              </>
            )}
            <span className="font-medium">{activeCategoryName}</span>
          </nav>
        )}
      />
      <section className="container max-w-8xl mx-auto px-4 py-8 bg-gray-50">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 px-0 py-4">
          {/* Mobile-mounted FilterList so the floating "Filtrer" button is available */}
          <div className="lg:hidden col-span-1">
            <FilterList
              parentCategoryName={parentCategoryName}
              groupedSubcategories={groupedSubcategories}
              activeSlug={activeSlug ?? category?.slug}
              onFilterSelect={handleFilterSelect}
              bgColor={effectiveCatalogColor}
              allCategories={allCategories}
              attributes={attributes}
              onAttributesChange={handleAttributesChange}
            />
          </div>

          <aside className="hidden lg:block lg:col-span-1 h-fit sticky top-42 bg-main rounded-lg">
            <FilterList
              parentCategoryName={parentCategoryName}
              groupedSubcategories={groupedSubcategories}
              activeSlug={activeSlug ?? category?.slug}
              onFilterSelect={handleFilterSelect}
              bgColor={effectiveCatalogColor}
              allCategories={allCategories}
              attributes={attributes}
              onAttributesChange={handleAttributesChange}
            />
          </aside>

          <section className="col-span-1 lg:col-span-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-gray-200 gap-4">
              <div>
                <p className="text-sm text-gray-500 mt-1">{products.length} produit{products.length > 1 ? 's' : ''} trouvé{products.length > 1 ? 's' : ''}</p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
              </div>
            </div>

            {loading && <div className="py-8 text-center">Chargement...</div>}
            {error && <div className="py-8 text-center text-red-600">{error}</div>}

            {products.length > 0 ? (
              <ProductGrid products={products} parentColorCataloge={category?.parent?.colorCataloge ?? category?.colorCataloge} />
            ) : (
              <div className="text-center py-20 rounded-lg shadow-sm bg-gray-100">
                <p className="text-xl font-medium text-gray-800">Aucun produit trouvé</p>
                <p className="mt-2 text-gray-500">Veuillez essayer une autre catégorie ou ajuster les filtres.</p>
              </div>
            )}
          </section>
        </div>
      </section>
    </div>
  );
}
